/** CVSS visualization constants and helpers. */

// Vinnie's CVSS 3.1 / CVSS 4.0 / SSVC score is this model's own score (no
// composite for those types). Mirrors libvinnie/constants.py.
export const VINNIE_LLM_MODEL = "gemma-4-31b-it";
export const VINNIE_LLM_KEY = `LLM/${VINNIE_LLM_MODEL}`;

export const SOURCE_COLORS = {
  NVD: "#00d4ff",
  LLM: "#ff6b6b",
  STATISTICAL: "#ffd93d",
  ENSEMBLE: "#bb86fc",
  COMPOSITE: "#ffffff",  // CWE composite
  [VINNIE_LLM_KEY]: "#ffffff",
};

// Extra colors for multiple LLM models
const EXTRA_COLORS = ["#ff9f43", "#a29bfe", "#55efc4", "#fd79a8", "#74b9ff"];

/** Get color for a source key, handling dynamic LLM/model keys. */
export function getSourceColor(key) {
  if (SOURCE_COLORS[key]) return SOURCE_COLORS[key];
  // Check base source (e.g. "LLM/qwen2.5:32b" → "LLM")
  const base = key.split("/")[0];
  if (SOURCE_COLORS[base]) {
    // Assign a deterministic extra color based on the key
    let hash = 0;
    for (let i = 0; i < key.length; i++) hash = (hash * 31 + key.charCodeAt(i)) | 0;
    return EXTRA_COLORS[Math.abs(hash) % EXTRA_COLORS.length];
  }
  return "#888";
}

export const SOURCE_LABELS = {
  NVD: "NVD (Official)",
  LLM: "LLM (AI)",
  STATISTICAL: "Statistical (Chi2)",
  ENSEMBLE: "Ensemble",
  COMPOSITE: "Composite",  // CWE composite
  [VINNIE_LLM_KEY]: "Vinnie (gemma)",
};

/** Get label for a source key, handling dynamic LLM/model keys. */
export function getSourceLabel(key) {
  if (SOURCE_LABELS[key]) return SOURCE_LABELS[key];
  // "LLM/qwen2.5:32b" → "LLM (qwen2.5:32b)"
  const slash = key.indexOf("/");
  if (slash !== -1) {
    const base = key.substring(0, slash);
    const model = key.substring(slash + 1);
    const baseLabel = SOURCE_LABELS[base] || base;
    return `${baseLabel.split(" (")[0]} (${model})`;
  }
  return key;
}

export const SEVERITY_COLORS = {
  CRITICAL: "#ff4444",
  HIGH: "#ff8800",
  MEDIUM: "#ffcc00",
  LOW: "#44bb44",
  NONE: "#888888",
};

export const SEVERITY_ORDER = ["NONE", "LOW", "MEDIUM", "HIGH", "CRITICAL"];

export const METRIC_LABELS = {
  AV: "Attack Vector",
  AC: "Attack Complexity",
  PR: "Privileges Required",
  UI: "User Interaction",
  S: "Scope",
  C: "Confidentiality",
  I: "Integrity",
  A: "Availability",
};

export const METRIC_VALUE_ORDER = {
  AV: ["N", "A", "L", "P"],
  AC: ["L", "H"],
  PR: ["N", "L", "H"],
  UI: ["N", "R"],
  S: ["U", "C"],
  C: ["H", "L", "N"],
  I: ["H", "L", "N"],
  A: ["H", "L", "N"],
};

export const METRIC_VALUE_LABELS = {
  AV: { N: "Network", A: "Adjacent", L: "Local", P: "Physical" },
  AC: { L: "Low", H: "High" },
  PR: { N: "None", L: "Low", H: "High" },
  UI: { N: "None", R: "Required" },
  S: { U: "Unchanged", C: "Changed" },
  C: { H: "High", L: "Low", N: "None" },
  I: { H: "High", L: "Low", N: "None" },
  A: { H: "High", L: "Low", N: "None" },
};

export const METRIC_MAX = {
  AV: 4,
  AC: 2,
  PR: 3,
  UI: 2,
  S: 2,
  C: 3,
  I: 3,
  A: 3,
};

// ── CVSS v4.0 constants ──────────────────────────────────────────────

export const METRIC_LABELS_V4 = {
  AV: "Attack Vector",
  AC: "Attack Complexity",
  AT: "Attack Requirements",
  PR: "Privileges Required",
  UI: "User Interaction",
  VC: "Vuln Confidentiality",
  VI: "Vuln Integrity",
  VA: "Vuln Availability",
  SC: "Subseq Confidentiality",
  SI: "Subseq Integrity",
  SA: "Subseq Availability",
};

export const METRIC_VALUE_ORDER_V4 = {
  AV: ["N", "A", "L", "P"],
  AC: ["L", "H"],
  AT: ["N", "P"],
  PR: ["N", "L", "H"],
  UI: ["N", "P", "A"],
  VC: ["H", "L", "N"],
  VI: ["H", "L", "N"],
  VA: ["H", "L", "N"],
  SC: ["H", "L", "N"],
  SI: ["H", "L", "N"],
  SA: ["H", "L", "N"],
};

export const METRIC_MAX_V4 = {
  AV: 4, AC: 2, AT: 2, PR: 3, UI: 3,
  VC: 3, VI: 3, VA: 3, SC: 3, SI: 3, SA: 3,
};

export const METRIC_VALUE_LABELS_V4 = {
  AV: { N: "Network", A: "Adjacent", L: "Local", P: "Physical" },
  AC: { L: "Low", H: "High" },
  AT: { N: "None", P: "Present" },
  PR: { N: "None", L: "Low", H: "High" },
  UI: { N: "None", P: "Passive", A: "Active" },
  VC: { H: "High", L: "Low", N: "None" },
  VI: { H: "High", L: "Low", N: "None" },
  VA: { H: "High", L: "Low", N: "None" },
  SC: { H: "High", L: "Low", N: "None" },
  SI: { H: "High", L: "Low", N: "None" },
  SA: { H: "High", L: "Low", N: "None" },
};

/** Get all unique sources from a cves array. */
export function getUniqueSources(cves) {
  const sources = new Set();
  cves.forEach((c) =>
    Object.keys(c.scores).forEach((s) => sources.add(s))
  );
  return Array.from(sources).sort();
}

/** Get the highest severity from a CVE's scores. */
export function maxSeverity(scores) {
  let max = -1;
  let label = "NONE";
  for (const info of Object.values(scores)) {
    const idx = SEVERITY_ORDER.indexOf(info.severity);
    if (idx > max) {
      max = idx;
      label = info.severity;
    }
  }
  return label;
}
